/**
 * Is there anything a visitor could actually READ in this waiver copy?
 *
 * SA-1671 pass 5. Pass 4 decided readability once, at the source — but it decided it from the
 * RAW string, so anything that is a non-empty string counted as content. Waiver copy is Markdown
 * that gets converted to HTML and sanitised before it reaches the page, and several non-empty
 * strings render to nothing at all:
 *
 *   `<!-- no terms -->`   an HTML comment — never displayed
 *   `<p></p>`             empty markup
 *   `&nbsp;` / `&#160;`   whitespace entities
 *   `<script>…</script>`  removed outright by the package's sanitiser
 *   `\u200b`              zero-width characters
 *
 * Each of those produced a waiver question whose body was a bare heading, which flipped that
 * side's content flag on, which showed and required that side's signature. So readability is
 * decided here from the RENDERED, sanitised body — after Markdown and HTML are resolved and
 * entities and whitespace are collapsed — not from the raw string.
 *
 * The render path this mirrors is the package's `markdown` question: the Markdown source is
 * converted to HTML, the result is sanitised (disallowed elements are removed with their whole
 * subtree), and the HTML is set as the question's inner HTML. `tests/waiverContent.test.ts`
 * mounts the real `<Survey>` and asserts, for every case below, that what this module calls
 * readable is exactly what shows up in the rendered DOM — so the two cannot drift apart.
 */
import Showdown from 'showdown';

/**
 * Same options as the app's survey-text converter (src/helpers/surveyTemplatesAll.tsx); the
 * waiver body is Markdown from the same authoring surface.
 */
const converter = new Showdown.Converter({ openLinksInNewWindow: true });

/**
 * Elements that never contribute anything visible, whatever they contain. `script`/`style` are
 * also what the package's sanitiser drops outright — matching it here keeps this check from
 * calling "readable" something the page then throws away.
 */
const INVISIBLE_ELEMENTS = 'script,style,template,noscript,iframe,object,embed,head,meta,link,title';

/**
 * Characters that occupy no visible space: NBSP and friends, zero-width marks, word joiner,
 * soft hyphen, BOM. Collapsed away before deciding whether any text is left.
 */
// eslint-disable-next-line no-misleading-character-class
const INVISIBLE_CHARS = /[\u00a0\u00ad\u034f\u061c\u180e\u2000-\u200f\u202a-\u202f\u2060-\u2064\u206a-\u206f\ufeff]/g;

/** Last-resort text extraction when there is no DOM (no SSR here today, but never throw). */
function textWithoutDom(html: string): string {
  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<(script|style|template|noscript|iframe|object|embed)\b[\s\S]*?<\/\1>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;|&#160;|&#xa0;/gi, ' ');
}

/** Collapses every run of whitespace (visible or not) and trims. */
function collapse(text: string): string {
  return text.replace(INVISIBLE_CHARS, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * The text a visitor would see if this Markdown were rendered as a waiver body: Markdown
 * resolved, invisible elements and comments dropped, entities decoded, whitespace collapsed.
 * Returns `''` when the copy renders to nothing at all.
 */
export function renderedWaiverBodyText(markdown: string): string {
  const html = converter.makeHtml(markdown);
  if (typeof DOMParser === 'undefined') return collapse(textWithoutDom(html));

  const body = new DOMParser().parseFromString(html, 'text/html').body;
  // Comments need no handling: they are not text nodes, so `textContent` never includes them —
  // `<!-- no terms -->` contributes nothing here, which is exactly the required outcome. (An
  // explicit comment-stripping pass was tried and removed: the mutation probe showed deleting it
  // changed no test, because it was a no-op.)
  for (const element of Array.from(body.querySelectorAll(INVISIBLE_ELEMENTS))) element.remove();
  return collapse(body.textContent ?? '');
}

/**
 * `true` when this copy renders something a visitor can actually perceive.
 *
 * Text is the usual case. An `<img>` with a real `src` also counts: a waiver authored as a
 * scanned image renders something the visitor reads, even though it contributes no text — and
 * treating it as absent would silently retire a waiver that works today. Empty markup, comments,
 * whitespace and entities that render blank do NOT count, whatever the raw string looks like.
 */
export function hasReadableWaiverBody(markdown: unknown): markdown is string {
  if (typeof markdown !== 'string' || markdown.trim().length === 0) return false;
  if (renderedWaiverBodyText(markdown).length > 0) return true;

  if (typeof DOMParser === 'undefined') return /<img[^>]+src\s*=\s*["']?[^"'\s>]+/i.test(markdown);
  const html = converter.makeHtml(markdown);
  const body = new DOMParser().parseFromString(html, 'text/html').body;
  for (const element of Array.from(body.querySelectorAll(INVISIBLE_ELEMENTS))) element.remove();
  return Array.from(body.querySelectorAll('img')).some(img => (img.getAttribute('src') ?? '').trim().length > 0);
}
